import React, { useEffect, useMemo, useRef, useState } from "react";
import { parse } from "marked";
import mermaid from "mermaid";
import MarkmapPopup from "./MarkmapPopup";
import "./MentorMindResponse.css";

let mermaidId = 0;

const normalizeHtmlAttributes = (html) =>
  html
    .replace(/_className_/g, "class")
    .replace(/className=/g, "class=")
    .replace(/\s+_key_=\{\d+\}/g, "")
    .replace(/\s+key=\{\d+\}/g, "");

const capCardsPerSection = (source, maxCards = 2) => {
  if (!source) return "";
  return source.replace(
    /(<(?:h[1-6]|p|div)[^>]*>\s*\[?\s*(?:Case Studies?|Initiatives?)\s*\]?\s*<\/(?:h[1-6]|p|div)>|#{1,6}\s*\[?\s*(?:Case Studies?|Initiatives?)\s*\]?)([\s\S]*?)(?=(?:<(?:h[1-6]|p|div)[^>]*>\s*\[?\s*(?:Case Studies?|Initiatives?|Keywords?)\s*\]?|#{1,6}\s*\[?\s*(?:Case Studies?|Initiatives?|Keywords?)\s*\]|```markmap|$))/gi,
    (match, heading, body) => {
      let count = 0;
      const capped = body.replace(
        /<div\s+(?:_className_|className|class)=["']card-uni["'][\s\S]*?<\/div>/gi,
        (card) => {
          count++;
          return count <= maxCards ? card : "";
        },
      );
      return heading + capped;
    },
  );
};

const suppressKeywords = (source) => {
  if (!source) return "";
  return source
    .replace(
      /(?:^|\n)\s*(?:#{1,6}|<(?:h[1-6]|p|div)[^>]*>)\s*\[?\s*Keywords?\s*\]?\s*(?:<\/(?:h[1-6]|p|div)>)?[^\n]*[\s\S]*?(?=(?:\n\s*#{1,6}|\n\s*<(?:h[1-6]|div)|```|$))/gi,
      "",
    )
    .replace(
      /(?:^|\n)\s*\*?\*?Keywords?:?\*?\*?[^\n]*\n?(?:[ \t]*(?:<p[^>]*class=["']keyword["'][^>]*>.*?<\/p>|[-*•][ \t]*[^\n]+)\n?)*/gi,
      "",
    )
    .replace(
      /<p[^>]*class=["'][^"']*\bkeyword\b[^"']*["'][^>]*>[\s\S]*?<\/p>/gi,
      "",
    )
    .replace(
      /<span[^>]*class=["'][^"']*\b(?:mm-keyword|keyword)\b[^"']*["'][^>]*>[\s\S]*?<\/span>/gi,
      "",
    );
};

const suppressAnswerWritingTakeaways = (source) => {
  if (!source) return "";
  return source
    .replace(
      /(?:^|\n)\s*(?:#{1,6}|<(?:h[1-6]|p|div)[^>]*>)\s*\[?\s*Answer[- ]writing\s+takeaways?:?\s*\]?\s*(?:<\/(?:h[1-6]|p|div)>)?[^\n]*[\s\S]*?(?=(?:\n\s*#{1,6}|\n\s*<(?:h[1-6]|div)|```|$))/gi,
      "",
    )
    .replace(
      /(?:^|\n)\s*\*?\*?Answer[- ]writing\s+takeaways?:?\*?\*?[^\n]*\n?(?:[ \t]*(?:[-*•]|\d+\.)?[ \t]*[^\n]+\n?)*/gi,
      "",
    );
};

const buildHtml = (raw) => {
  let source = String(raw || "");
  const markmap =
    source.match(/```markmap\s*\n([\s\S]*?)```/i)?.[1]?.trim() || "";

  source = source.replace(
    /^#{1,6}[ \t]*\[?[ \t]*Mindmap[ \t]*\]?[^\n]*[\s\S]*/im,
    "",
  );

  source = source.replace(/```html\s*\n?([\s\S]*?)```/gi, "$1");
  source = capCardsPerSection(source, 2);
  source = suppressKeywords(source);
  source = suppressAnswerWritingTakeaways(source);

  source = source.replace(
    /<h[1-6][^>]*>\s*\[?\s*(?:Case Studies?|Initiatives?|Keywords?|Answer[- ]writing\s+takeaways?)\s*\]?\s*<\/h[1-6]>/gi,
    "",
  );

  source = normalizeHtmlAttributes(source);

  let html = "";
  try {
    html = parse(source, { async: false, gfm: true }) || "";
  } catch (error) {
    console.error("Error parsing Mentormind markdown:", error);
  }

  html = html
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(
      /<p[^>]*class=["'][^"']*\bkeyword\b[^"']*["'][^>]*>([\s\S]*?)<\/p>/gi,
      "",
    )
    .replace(
      /<h[1-6][^>]*>\s*Answer[- ]writing\s+takeaways?:?\s*<\/h[1-6]>[\s\S]*?(?=<h[1-6]|<div\s+class=["']mm-login-prompt|$)/gi,
      "",
    )
    .replace(
      /<p[^>]*>\s*<strong>\s*Answer[- ]writing\s+takeaways?:?\s*<\/strong>[\s\S]*?<\/p>/gi,
      "",
    );

  return { html, markmap };
};

const MentorMindResponse = ({ content, onCardClick, onLoginClick }) => {
  const containerRef = useRef(null);
  const [showMarkmap, setShowMarkmap] = useState(false);
  const { html, markmap } = useMemo(() => buildHtml(content), [content]);

  useEffect(() => {
    setShowMarkmap(false);
  }, [content]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const blocks = container.querySelectorAll("pre code.language-mermaid");
    if (!blocks.length) return;

    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
    });

    blocks.forEach(async (codeBlock) => {
      const pre = codeBlock.closest("pre");
      if (!pre) return;
      const code = codeBlock.textContent.trim();
      if (!code) return;

      try {
        const id = `mm-mermaid-${++mermaidId}`;
        const { svg } = await mermaid.render(id, code);
        const wrapper = document.createElement("div");
        wrapper.className = "mm-mermaid";
        wrapper.innerHTML = svg;
        if (pre.parentNode) pre.parentNode.replaceChild(wrapper, pre);
      } catch (error) {
        console.error("Unable to render Mermaid diagram:", error);
      }
    });
  }, [html]);

  const handleClick = (event) => {
    if (!onCardClick) return;
    const card = event.target.closest(".card-uni");
    if (!card) return;
    const country = card.querySelector(".country")?.textContent || "";
    const heading = card.querySelector(".heading")?.textContent || "";
    if (!heading) return;
    onCardClick(
      `Give me a detailed explanation of '${heading}'${
        country ? ` in '${country}'` : ""
      }.`,
    );
  };

  return (
    <div className="mm-response">
      <div
        ref={containerRef}
        className="mm-response-body"
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={handleClick}
      />

      <div className="mm-login-prompt-banner">
        <div className="mm-login-prompt-content">
          <span className="mm-login-prompt-icon">✨</span>
          <p className="mm-login-prompt-text">
            <strong>Want deeper insights?</strong> Log in for comprehensive
            responses featuring more case studies, policy initiatives,
            interactive mindmaps, and thematic PYQ analysis.
          </p>
        </div>
        {onLoginClick && (
          <button
            type="button"
            className="mm-login-prompt-btn"
            onClick={onLoginClick}
          >
            <span>Log in for Full Access</span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* {markmap && (
        <button
          type="button"
          className="mm-mindmap-btn"
          onClick={() => setShowMarkmap(true)}
        >
          View Mindmap
        </button>
      )} */}

      {/* {showMarkmap && markmap && (
        <MarkmapPopup
          markmapContent={markmap}
          onClose={() => setShowMarkmap(false)}
        />
      )} */}
    </div>
  );
};

export default MentorMindResponse;
